/**
 * ItemSection — generic equipment item section.
 *
 * Composes ItemCards into a panel with Add input and Mark OOS dropdown.
 * Used by RecorderPanel, AccessBadgePanel, SatPhonePanel.
 */
import { useState } from 'react'
import { PanelSection, Button } from '../ui'
import { markDirty } from '../../stores/sync'
import type { GenericItem } from '../../stores/desktop'
import ItemCard from './ItemCard'

interface ItemSectionProps {
  items: GenericItem[]
  setItems: (v: GenericItem[]) => void
  title: string
  image: string
  itemLabel: string
  cols?: number
  cardVariant?: 'officer' | 'spare'
}

export default function ItemSection({
  items,
  setItems,
  title,
  image,
  itemLabel,
  cols = 3,
  cardVariant,
}: ItemSectionProps) {
  const [showOosMenu, setShowOosMenu] = useState(false)
  const [addInput, setAddInput] = useState('')

  function update(id: string, patch: Partial<GenericItem>) {
    markDirty()
    setItems(items.map(x => (x.id === id ? { ...x, ...patch } : x)))
  }

  function addItem() {
    const id = addInput.trim()
    if (!id) return
    if (items.some(x => x.id === id)) {
      alert(`${itemLabel} already exists`)
      return
    }
    markDirty()
    setItems([...items, { id, available: true, assignedTo: '' }])
    setAddInput('')
  }

  function toggleOos(id: string) {
    markDirty()
    setItems(items.map(x => (x.id === id ? { ...x, oos: !x.oos } : x)))
  }

  const oosCount   = items.filter(x => x.oos).length
  const outCount   = items.filter(x => !x.oos && x.assignedTo && !x.available).length
  const availCount = items.filter(x => !x.oos && x.available).length

  const actions = (
    <>
      <input
        type="text"
        value={addInput}
        onChange={e => setAddInput(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && addItem()}
        placeholder={`${itemLabel} ID`}
        style={{
          width: 80,
          background: 'var(--bg-deep)',
          border: '1px solid var(--border)',
          borderRadius: 6,
          color: 'var(--text-primary)',
          fontSize: 12,
          padding: '4px 8px',
          outline: 'none',
        }}
      />
      <Button variant="primary" size="sm" onClick={addItem}>
        Add
      </Button>
      <div style={{ position: 'relative' }}>
        <Button variant="ghost" size="sm" onClick={() => setShowOosMenu(m => !m)}>
          Mark OOS
          {oosCount > 0 && <span style={{ color: '#ef4444', fontWeight: 700 }}>({oosCount})</span>}
        </Button>
        {showOosMenu && (
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: '110%',
              width: 200,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              boxShadow: '0 10px 25px rgba(0,0,0,0.4)',
              zIndex: 999,
              maxHeight: 280,
              overflowY: 'auto',
            }}
          >
            <div style={{ padding: '8px 12px' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
                Select to mark OOS
              </div>
              {items.map(x => (
                <label
                  key={x.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 8px',
                    cursor: 'pointer',
                    borderRadius: 6,
                    fontSize: 13,
                    color: 'var(--text-primary)',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!x.oos}
                    onChange={() => toggleOos(x.id)}
                    style={{ width: 15, height: 15, accentColor: '#2563eb' }}
                  />
                  {itemLabel} {x.id}
                </label>
              ))}
            </div>
            <div style={{ borderTop: '1px solid var(--border)', padding: 8 }}>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowOosMenu(false)}
                style={{ width: '100%', justifyContent: 'center' }}
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  )

  return (
    <PanelSection
      title={title}
      counts={{ available: availCount, out: outCount, oos: oosCount }}
      actions={actions}
    >
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12 }}>
        {items.map(item => (
          <ItemCard
            key={item.id}
            item={item}
            image={image}
            itemLabel={itemLabel}
            variant={cardVariant}
            onUpdate={patch => update(item.id, patch)}
          />
        ))}
      </div>
    </PanelSection>
  )
}
